class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.onWheel = this.onWheel.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  getScrollContainer() {
    if (this.classList.contains('is-empty')) return null;
    return (
      this.querySelector('.drawer__inner') ||
      this.querySelector('.cart-drawer__form') ||
      this.querySelector('cart-drawer-items') ||
      this.querySelector('.drawer__cart-items-wrapper')
    );
  }

  isScrollable(element) {
    if (!element) return false;
    return element.scrollHeight > element.clientHeight + 1;
  }

  lockBodyScroll() {
    if (document.body.classList.contains('cart-drawer-open')) return;

    this.savedScrollY = window.scrollY || window.pageYOffset || 0;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      const header = document.querySelector('.site-header, header-drawer, .header-wrapper');
      if (header) header.style.paddingRight = `${scrollbarWidth}px`;
    }

    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'none';

    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.body.classList.add('overflow-hidden', 'cart-drawer-open');

    window.addEventListener('wheel', this.onWheel, { passive: false });
    window.addEventListener('touchstart', this.onTouchStart, { passive: false });
    window.addEventListener('touchmove', this.onTouchMove, { passive: false });
    window.addEventListener('keydown', this.onKeyDown, { passive: false });
  }

  unlockBodyScroll() {
    if (!document.body.classList.contains('cart-drawer-open')) return;

    window.removeEventListener('wheel', this.onWheel, { passive: false });
    window.removeEventListener('touchstart', this.onTouchStart, { passive: false });
    window.removeEventListener('touchmove', this.onTouchMove, { passive: false });
    window.removeEventListener('keydown', this.onKeyDown, { passive: false });

    document.documentElement.style.overflow = '';
    document.documentElement.style.overscrollBehavior = '';

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');

    const header = document.querySelector('.site-header, header-drawer, .header-wrapper');
    if (header) header.style.paddingRight = '';

    if (typeof this.savedScrollY === 'number') {
      window.scrollTo(0, this.savedScrollY);
    }
  }

  onWheel(event) {
    const scrollContainer = this.getScrollContainer();

    if (!scrollContainer || !this.isScrollable(scrollContainer) || !scrollContainer.contains(event.target)) {
      event.preventDefault();
      return;
    }

    const delta = event.deltaY;
    const scrollTop = scrollContainer.scrollTop;
    const maxScroll = scrollContainer.scrollHeight - scrollContainer.clientHeight;

    if (delta < 0 && scrollTop <= 0) {
      event.preventDefault();
    } else if (delta > 0 && scrollTop >= maxScroll - 1) {
      event.preventDefault();
    }
  }

  onTouchStart(event) {
    if (event.touches.length !== 1) return;
    this.touchStartY = event.touches[0].clientY;
  }

  onTouchMove(event) {
    if (event.touches.length !== 1) return;
    if (event.target.closest?.('.hf-cart-recs__viewport')) return;
    const scrollContainer = this.getScrollContainer();

    if (!scrollContainer || !this.isScrollable(scrollContainer) || !scrollContainer.contains(event.target)) {
      event.preventDefault();
    }
    // SE 2026-11-01 #store-agent 1566340209832890519 - no preventDefault inside the scroller: at either
    // end a 1px finger wobble cancelled the whole swipe. overscroll-behavior:contain keeps the page still.
  }

  onKeyDown(event) {
    const keys = ['Space', 'PageUp', 'PageDown', 'End', 'Home', 'ArrowUp', 'ArrowDown'];
    if (keys.includes(event.code)) {
      const activeTag = document.activeElement ? document.activeElement.tagName.toLowerCase() : '';
      if (['input', 'textarea', 'select'].includes(activeTag)) return;

      const scrollContainer = this.getScrollContainer();
      if (!scrollContainer || !scrollContainer.contains(document.activeElement) || !this.isScrollable(scrollContainer)) {
        event.preventDefault();
      }
    }
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.lockBodyScroll();

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.unlockBodyScroll();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents',
      }
    ].filter((section) => section.section && document.getElementById(section.id));
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
